import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Banknote, CreditCard, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { formatCurrency } from './utils';

export default function DepositDialog({
  open,
  onOpenChange,
  reservation,
  depositData,
  setDepositData,
  onSave
}) {
  if (!reservation) return null;
  
  const totalAmount = reservation.total_amount_mad || reservation.total_amount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-green-600" />
            Gestion de l'acompte
          </DialogTitle>
        </DialogHeader>
        
        {/* Reservation summary card */}
        <div className="bg-gradient-to-r from-slate-50 to-slate-100 p-4 rounded-lg border">
          <div className="flex justify-between items-start">
            <div>
              <p className="font-semibold text-lg">
                {reservation.guest?.first_name} {reservation.guest?.last_name}
              </p>
              <p className="text-sm text-muted-foreground">
                {format(new Date(reservation.check_in), 'dd MMM')} → {format(new Date(reservation.check_out), 'dd MMM yyyy')}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Total réservation</p>
              <p className="text-xl font-bold text-primary">
                {formatCurrency(totalAmount, 'MAD')}
              </p>
            </div>
          </div>
        </div>
        
        {/* Form fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium">Montant reçu (MAD)</Label>
            <Input
              type="number"
              placeholder="0"
              className="mt-1"
              value={depositData.deposit_amount}
              onChange={(e) => setDepositData({ ...depositData, deposit_amount: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Date de réception</Label>
            <Input
              type="date"
              className="mt-1"
              value={depositData.deposit_date}
              onChange={(e) => setDepositData({ ...depositData, deposit_date: e.target.value })}
            />
          </div>
        </div>
        
        <div>
          <Label className="text-sm font-medium">Date rappel solde</Label>
          <Input
            type="date"
            className="mt-1"
            value={depositData.balance_reminder_date}
            onChange={(e) => setDepositData({ ...depositData, balance_reminder_date: e.target.value })}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Une alerte apparaîtra sur le tableau de bord à cette date
          </p>
        </div>

        {/* Balance summary */}
        {depositData.deposit_amount > 0 && (
          <div className="flex items-center justify-between bg-emerald-50 p-3 rounded-lg border border-emerald-200">
            <span className="text-sm text-emerald-700">Solde restant dû :</span>
            <span className="text-lg font-bold text-emerald-700">
              {formatCurrency(
                Math.max(0, totalAmount - parseFloat(depositData.deposit_amount || 0)), 
                'MAD'
              )}
            </span>
          </div>
        )}

        {/* Quick fill button */}
        {parseFloat(depositData.deposit_amount || 0) < totalAmount && (
          <Button 
            type="button"
            variant="outline" 
            size="sm"
            className="w-full border-dashed"
            onClick={() => setDepositData({ 
              ...depositData, 
              deposit_amount: String(totalAmount),
              deposit_date: depositData.deposit_date || new Date().toISOString().split('T')[0]
            })}
            data-testid="mark-full-deposit-btn"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Remplir avec le montant total
          </Button>
        )}
        
        {/* Footer with clearer buttons */}
        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between pt-4 border-t">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onSave(false)}
              disabled={!depositData.deposit_amount || parseFloat(depositData.deposit_amount) <= 0}
            >
              Enregistrer
            </Button>
            <Button 
              size="sm"
              onClick={() => onSave(true)}
              className="bg-green-600 hover:bg-green-700"
              disabled={!depositData.deposit_amount || parseFloat(depositData.deposit_amount) <= 0}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Valider + Créer paiement
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
